import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatLongDate,
  toDateKey,
  toWeekKey,
  type ScheduleLesson,
  type TaskItem,
} from '@skolveckan/shared';
import { DayTimeline } from '../components/DayTimeline';
import { EmptyState } from '../components/EmptyState';
import { IconChevronRight, IconClock, IconStar } from '../components/Icons';
import { LessonModal } from '../components/LessonModal';
import { NowCard } from '../components/NowCard';
import { TaskRow } from '../components/TaskRow';
import { useApi } from '../hooks/useApi';
import { nowMinutes, useNow } from '../hooks/useNow';
import { api } from '../lib/api';
import { subjectColors } from '../lib/subjects';

export function TodayPage() {
  const now = useNow();
  const nowMin = nowMinutes(now);
  const today = toDateKey(now);
  const [openLessonId, setOpenLessonId] = useState<string | null>(null);

  const day = useApi(() => api.day(today), [today]);
  const tasks = useApi(() => api.tasks('today'), [today]);
  const [taskOverrides, setTaskOverrides] = useState<Record<string, 'todo' | 'done'>>({});

  const lessons = day.data?.lessons ?? [];
  const current = lessons.find((l) => nowMin >= l.startMin && nowMin < l.endMin) ?? null;
  const next = lessons.find((l) => l.startMin > nowMin && l.kind === 'lesson') ?? null;

  const todayTasks = (tasks.data?.groups ?? [])
    .flatMap((g) => g.items)
    .map((t) => ({ ...t, state: taskOverrides[t.id] ?? t.state }));
  const doneCount = todayTasks.filter((t) => t.state === 'done').length;
  const exam = todayTasks.find((t) => t.isExam);

  async function toggleTask(task: TaskItem) {
    const nextState = task.state === 'done' ? 'todo' : 'done';
    setTaskOverrides((prev) => ({ ...prev, [task.id]: nextState }));
    try {
      await api.setTaskState(task.id, nextState);
    } catch {
      // Rulla tillbaka om servern säger nej — annars ljuger kryssrutan.
      setTaskOverrides((prev) => ({ ...prev, [task.id]: task.state }));
    }
  }

  return (
    <>
      <header className="page__head">
        <h1>{capitalize(formatLongDate(today))}</h1>
        <span className="chip">Vecka {toWeekKey(now).split('-W')[1]}</span>
        <div className="grow" />
        <span className="page__clock">
          <IconClock size={15} />
          Klockan är <strong>{formatClock(now)}</strong>
        </span>
      </header>

      <NowCard
        current={current}
        next={next}
        nowMin={nowMin}
        isToday
        onOpen={(lesson) => setOpenLessonId(lesson.id)}
      />

      <div className="page__columns">
        <section className="card panel">
          <header className="panel__head">
            <span className="panel__title">Dagens lektioner</span>
            <span className="grow" />
            <span className="muted">08:00 – 16:00</span>
          </header>

          {day.loading && <p className="muted">Hämtar schemat…</p>}
          {!day.loading && lessons.length === 0 && (
            <EmptyState
              title="Inga lektioner idag"
              body="Det ligger inget schema på den här dagen. Är det lov, eller saknas schemat i admin?"
            />
          )}
          {lessons.length > 0 && (
            <DayTimeline
              lessons={lessons}
              nowMin={nowMin}
              isToday
              onOpen={(lesson) => setOpenLessonId(lesson.id)}
            />
          )}
        </section>

        <div className="page__aside">
          {next && <NextLessonCard lesson={next} />}

          <section className="card panel panel--grow">
            <header className="panel__head">
              <span className="label">Att göra idag</span>
              <span className="grow" />
              <span className="chip chip--accent">
                {doneCount} av {todayTasks.length} klara
              </span>
            </header>

            {todayTasks.length === 0 ? (
              <EmptyState
                title="Inget att göra idag"
                body="Läraren har inte lagt in några uppgifter på dagens lektioner."
              />
            ) : (
              <div className="tasklist">
                {todayTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onToggle={toggleTask}
                    onOpenLesson={setOpenLessonId}
                  />
                ))}
              </div>
            )}

            <div className="grow" />
            <Link to="/uppgifter" className="button button--ghost">
              Alla uppgifter
              <IconChevronRight size={14} />
            </Link>
          </section>

          {exam && (
            <section className="card warncard">
              <IconStar />
              <div className="stack">
                <span className="warncard__title">{exam.title}</span>
                <span className="warncard__body">{exam.subtitle ?? 'Prov'}</span>
              </div>
            </section>
          )}
        </div>
      </div>

      <LessonModal lessonId={openLessonId} onClose={() => setOpenLessonId(null)} />
    </>
  );
}

function NextLessonCard({ lesson }: { lesson: ScheduleLesson }) {
  const colors = subjectColors(lesson.subject?.colorKey);
  return (
    <section className="card panel">
      <span className="label">Strax: nästa lektion</span>
      <div className="row" style={{ gap: 9, marginTop: 9 }}>
        <span className="dot" style={{ background: colors.line }} />
        <span className="nextlesson__subject" style={{ color: colors.ink }}>
          {lesson.subject?.name ?? 'Lektion'}
        </span>
        <span className="grow" />
        <span className="muted">
          {String(Math.floor(lesson.startMin / 60)).padStart(2, '0')}:
          {String(lesson.startMin % 60).padStart(2, '0')}
          {lesson.room ? ` · ${lesson.room}` : ''}
        </span>
      </div>
      {lesson.title && <p className="nextlesson__title">{lesson.title}</p>}
    </section>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatClock(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
